"use client"

import React, { createContext, useContext, useState, ReactNode } from 'react'
import dynamic from 'next/dynamic'

// Dynamic import to avoid SSR issues
const AigencyModal = dynamic(
  () => import('./AigencyModal'),
  { ssr: false }
)

interface Assistant {
  _id: string
  vapiId: string
  name: string
  type?: string
  role?: string
  personality?: string
  voice?: {
    provider: string
    voiceId: string
  }
  firstMessage?: string
  instructions?: string
}

interface TeamMember {
  _id: string
  name: string
  displayName?: string
  role?: string
  calendlyUrl?: string
  linkedinUrl?: string
  twitterUrl?: string
  imageSrc?: string
  emailDestination?: string
}

type ModalEntity = (Assistant & { modalType: 'assistant' }) | (TeamMember & { modalType: 'human' })

interface AigencyContextType {
  activeEntity: ModalEntity | null
  isModalOpen: boolean
  openConversation: (entity: Assistant | TeamMember, modalType: 'assistant' | 'human') => void
  closeConversation: () => void
}

const AigencyContext = createContext<AigencyContextType | undefined>(undefined)

export function useAigency() {
  const context = useContext(AigencyContext)
  if (context === undefined) {
    throw new Error('useAigency must be used within an AigencyProvider')
  }
  return context
}

interface AigencyProviderProps {
  children: ReactNode
}

export function AigencyProvider({ children }: AigencyProviderProps) {
  const [activeEntity, setActiveEntity] = useState<ModalEntity | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openConversation = (entity: Assistant | TeamMember, modalType: 'assistant' | 'human') => {
    const entityWithType = { ...entity, modalType } as ModalEntity
    setActiveEntity(entityWithType)
    setIsModalOpen(true)
  }

  const closeConversation = () => {
    setIsModalOpen(false)
    // Keep entity in state briefly for animation
    setTimeout(() => {
      setActiveEntity(null)
    }, 300)
  }

  return (
    <AigencyContext.Provider 
      value={{ 
        activeEntity, 
        isModalOpen,
        openConversation, 
        closeConversation
      }}
    >
      {children}
      
      {/* Global AigencyModal */}
      {activeEntity && (
        <AigencyModal
          entity={activeEntity}
          isOpen={isModalOpen}
          onClose={closeConversation}
        />
      )}
    </AigencyContext.Provider>
  )
}